import * as THREE from 'three';

/**
 * Tracks every object the bridge has created, so commands can address them by a
 * stable, human-typable id like `sphere_1`.
 *
 * The id is written in three places on purpose:
 *   userData.anvilId   — what the registry looks up
 *   userData.anvilType — what kind of primitive it was, for `listObjects`
 *   object.name        — so the name survives glTF export into Blender/UE5
 *
 * That last one matters for the pipeline: an unnamed mesh imports as
 * "Object.001" and you lose track of what it was.
 */
export function createRegistry(contentGroup) {
  /** @type {Map<string, THREE.Object3D>} */
  const objects = new Map();
  const counters = new Map();

  /**
   * Mint the next `type_n` that is not already in the registry.
   *
   * The counter is per-type and only ever moves forward, but uniqueness is
   * global: `box_7` may still be owned by a mesh after `toEditableMesh`, and
   * a later `addBox()` must not reuse that id. Skip occupied candidates until
   * one is free, then leave the counter at the n that was actually issued.
   */
  function nextId(type) {
    let n = counters.get(type) ?? 0;
    let id;
    do {
      n += 1;
      id = `${type}_${n}`;
    } while (objects.has(id));
    counters.set(type, n);
    return id;
  }

  /**
   * Reserve a caller-supplied id.
   *
   * Used when the bridge rebuilds a scene from its log: ids are pinned in the
   * log so an object keeps the name you've been addressing it by, instead of
   * being renumbered because an earlier object was deleted.
   *
   * The counter is advanced past anything matching `type_n`, so ids handed out
   * later in the same session can't collide with a reserved one.
   */
  function reserveId(type, id) {
    if (objects.has(id)) throw new Error(`id '${id}' is already taken`);
    const match = new RegExp(`^${type}_(\\d+)$`).exec(id);
    if (match) counters.set(type, Math.max(counters.get(type) ?? 0, Number(match[1])));
    return id;
  }

  function add(object, type, params = {}, explicitId = null, parent = contentGroup) {
    const id = explicitId ? reserveId(type, explicitId) : nextId(type);
    object.userData.anvilId = id;
    object.userData.anvilType = type;
    object.userData.anvilParams = params;
    // A caller-supplied name wins; otherwise the id doubles as the name.
    if (!object.name) object.name = id;
    object.castShadow = true;
    object.receiveShadow = true;
    parent.add(object);
    objects.set(id, object);
    return id;
  }

  /** Throws with a useful message rather than returning undefined. */
  function require(id) {
    const object = objects.get(id);
    if (!object) {
      const known = [...objects.keys()];
      throw new Error(
        `no object with id '${id}'` +
          (known.length ? ` — scene has: ${known.join(', ')}` : ' — scene is empty'),
      );
    }
    return object;
  }

  function dispose(object) {
    object.traverse((node) => {
      node.geometry?.dispose();
      const material = node.material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose();
    });
  }

  /** Registered ids directly inside this object, in scene order. */
  function childIdsOf(object) {
    return object.children.filter((c) => c.userData.anvilId).map((c) => c.userData.anvilId);
  }

  /** Every registered id at or below this object, deepest last. */
  function descendantIds(object) {
    const ids = [];
    object.traverse((node) => {
      if (node !== object && node.userData.anvilId) ids.push(node.userData.anvilId);
    });
    return ids;
  }

  /**
   * Remove an object and everything registered inside it.
   *
   * Deleting a group takes its members with it — they live inside it, so
   * leaving them behind as invisible orphans of a vanished parent would be
   * worse than the alternative. Ungroup first if you want to keep them.
   */
  function remove(id) {
    const object = require(id);
    const removed = [...descendantIds(object), id];

    // Detach from whatever actually holds it, which is a group once grouping is
    // in play — assuming contentGroup here would silently leave grouped objects
    // in the scene after a delete.
    object.parent?.remove(object);
    // Without this, a long live-editing session quietly leaks GPU memory —
    // easy to miss precisely because the app never reloads.
    dispose(object);
    for (const gone of removed) objects.delete(gone);
    return removed;
  }

  function clear() {
    const ids = [...objects.keys()];
    // Removing a group already takes its members, so only walk the roots and
    // skip anything a previous removal swallowed.
    for (const id of ids) if (objects.has(id)) remove(id);
    counters.clear();
    return ids;
  }

  /** Move an object to a new parent, keeping it where it appears on screen. */
  function reparent(object, parent) {
    // attach(), not add(): it compensates the local transform for the new
    // parent's world matrix, so grouping doesn't make everything jump.
    parent.attach(object);
  }

  function describe(object) {
    const id = object.userData.anvilId;
    return {
      id,
      type: object.userData.anvilType,
      name: object.name,
      position: round(object.position.toArray()),
      // Degrees, not radians. Radians in a hand-typed CLI argument are a
      // needless trip hazard, so the whole surface speaks degrees.
      rotationDeg: round([
        THREE.MathUtils.radToDeg(object.rotation.x),
        THREE.MathUtils.radToDeg(object.rotation.y),
        THREE.MathUtils.radToDeg(object.rotation.z),
      ]),
      scale: round(object.scale.toArray()),
      color: object.material?.color ? `#${object.material.color.getHexString()}` : null,
      // Carried so a duplicate reproduces the original's surface, not just its
      // colour — a copy that quietly lost its metalness would be a nasty
      // little bug to chase.
      roughness: object.material?.roughness ?? null,
      metalness: object.material?.metalness ?? null,
      parent: object.parent?.userData?.anvilId ?? null,
      children: childIdsOf(object),
      params: object.userData.anvilParams ?? {},
    };
  }

  /**
   * Every object, parents before their children.
   *
   * Traversal order rather than insertion order, so the output reads as the
   * tree it now is — and so a replay that recreates it in this order always
   * has the parent on hand before the child needs it.
   */
  function list() {
    const described = [];
    contentGroup.traverse((node) => {
      if (node.userData.anvilId) described.push(describe(node));
    });
    return described;
  }

  return {
    add,
    require,
    get: (id) => objects.get(id),
    remove,
    clear,
    list,
    describe,
    reparent,
    childIdsOf,
    descendantIds,
    has: (id) => objects.has(id),
    parentIdOf: (id) => objects.get(id)?.parent?.userData?.anvilId ?? null,
    get size() {
      return objects.size;
    },
  };
}

/** Keeps `listObjects` output readable instead of full float noise. */
function round(values, places = 4) {
  const factor = 10 ** places;
  return values.map((v) => Math.round(v * factor) / factor);
}
